import { action } from '@ember/object';
import { service } from '@ember/service';

import type { PopupData, RequestError, Service } from 'webapp-types';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import { Severity } from 'webapp/services/logger';

export default class BillingPopupServicesConfirmComponent extends PopupInnerModalComponent<PopupData.Billing.ServicesConfirm> {
	@service('account') declare accountService: Service.Account;
	@service('billing') declare billingService: Service.Billing;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('logger') declare loggerService: Service.Logger;
	@service('request') declare requestService: Service.Request;

	get model() {
		return this.popupData.model;
	}

	@action
	save() {
		if (this.submitInProgress) {
			return;
		}

		const layer = this.infolayerService.loading('Confirming your service purchase...'),
			productId = this.model.id as string;

		this.submitInProgress = true;

		this.billingService
			.addService(productId)
			.then(
				() => {
					layer.ok(
						'Service successfully purchased! We will contact you soon with more details.',
					);

					this.loggerService.logError(
						'Service purchase success',
						{ productId },
						Severity.INFO,
					);

					this.close();
				},
				(errorData: RequestError) => {
					if (errorData.isBlockedOnDemo) {
						layer.close();
						this.accountService.handleDemo();
						return;
					}

					const errorMsg =
						errorData.errors?.[0]?.message || 'Error when ordering service.';

					layer.error(errorMsg);

					this.loggerService.logError(
						'Service purchase error',
						{ productId },
						Severity.CRITICAL,
					);
				},
			)
			.finally(() => {
				this.submitInProgress = false;
			})
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}
	<template>
		<div class="title">Confirm your purchase</div>

		<div class="content">

			<p class="p-top">Selected service:</p>

			<div class="account-billing-services">
				<div
					class="account-billing-services-option account-billing-services-option-{{this.model.id}}
						selected"
				>
					<CommonUiIconComponent @name={{this.model.icon}} @size="xl" />
					{{#if this.model.price}}
						<span class="price">
							{{this.model.priceFormatted}}
						</span>
					{{else}}
						<div class="free-tag"></div>
					{{/if}}
					<p>
						<strong>{{this.model.name}}</strong>
						<br />
						{{{this.model.description}}}
					</p>
				</div>
			</div>

			<p class="terms">
				By confirming you agree to our
				<a
					class="link link--secondary link--underline"
					href="https://databox.com/terms-of-service"
					target="_blank"
				>
					Terms of Service
				</a>.
			</p>

			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Purchase now"
						@extraClass="save"
						@onTrigger={{this.save}}
						@submitInProgress={{this.submitInProgress}}
					/>
				</div>
			</div>

		</div>
	</template>
}
